import { useCallback, useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import { useForm, Controller } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import {
  IconLoader,
  ButtonPrimary,
  ToggleInput,
  ResponseError,
  responseErrorsResolver,
  Select,
  SelectType,
  SelectListPosition,
  AutocompleteMultiple,
  getResponseErrors,
  InputText,
  InputNumber,
  InputSize,
  PermissionEnum,
  IconInfo,
  IconChain,
  ButtonSecondary,
  ButtonSize,
  IconExpand,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useEditBeneficiarySettingsMutation,
  useLazyGetPSPDetailsQuery,
  useLazyGetCASPAliasesQuery,
  useCreateCASPAliasMutation,
  useDeleteCASPAliasMutation,
  useLazyGetAMLBotAssetsQuery,
} from '../settings/store/services/details';
import {
  TransactionsSettingsKeys,
  BeneficiariesSettingsVormValues,
  CASPAliasFormValues,
  CASPAlias,
} from '../settings/types';
import {
  useLazyGetEmployeesTransactionsTypesQuery,
  useLazyGetEmployeesCASPQuery,
} from '../settings/store/services/transactions-types';
import { setCaspAliases } from '../settings/store/slices/details';
import { useLazyGetActiveCurrenciesQuery } from '../../store/dictionaries/service';
import CaspAlias from '../../components/settings/casp-alias/casp-alias';

import cn from '../settings/styles.module.css';

const getTransactionTypeName = (name: string) => {
  let transactionTypeName: string;

  switch (name) {
    case 'INCOMING':
      transactionTypeName = 'Incoming';
      break;
    case 'OUTGOING':
      transactionTypeName = 'Outgoing';
      break;
    default:
      transactionTypeName = name;
      break;
  }

  return transactionTypeName;
};

const TransactionOptions = () => {
  const [defaultFormValues, setDefaultFormValues] =
    useState<BeneficiariesSettingsVormValues>();
  const [errorList, setErrorList] = useState('');
  const [caspAliasesList, setCaspAliasesList] = useState<CASPAliasFormValues[]>(
    [],
  );
  const [selectedCaspAliasId, setSelectedCaspAliasId] = useState('');
  const [creationCaspAliasErrors, setCreationCaspAliasErrors] = useState<
    { id: string; error: ResponseError }[]
  >([]);

  const dispatch = useAppDispatch();

  const [getBeneficiariesSettings, { isLoading, isFetching }] =
    useLazyGetPSPDetailsQuery();
  const [edit, { isLoading: editIsLoading }] =
    useEditBeneficiarySettingsMutation();
  const [globalError, setGlobalError] = useState('');
  const [getTransactionTypesList, { isLoading: transactionTypesIsLoading }] =
    useLazyGetEmployeesTransactionsTypesQuery();
  const [getActiveCurrencies, { isLoading: activeCurrenciesIsLoading }] =
    useLazyGetActiveCurrenciesQuery();
  const [
    getCASPAliases,
    { isLoading: caspAliasesIsLoasing, isFetching: caspAliasesIsFetching },
  ] = useLazyGetCASPAliasesQuery();
  const [getCaspLists] = useLazyGetEmployeesCASPQuery();
  const [getAMLBotAssets] = useLazyGetAMLBotAssetsQuery();
  const [createCaspAliases, { isLoading: createCaspAliasInProgress }] =
    useCreateCASPAliasMutation();
  const [
    deleteCaspAlias,
    {
      isLoading: deleteCaspAliasInProgress,
      isSuccess: deleteCaspAliasIsSuccess,
    },
  ] = useDeleteCASPAliasMutation();

  const {
    settings: {
      details: { pspDetails, caspAliases, amlBotAssets },
      transactionsTypes: { values: transactionsTypes, casp },
    },
    administrationDictionaries: { activeCurrencies },
  } = useAppSelector((state) => state);
  const { activePermissions } = useAppSelector((state) => state.root);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<BeneficiariesSettingsVormValues>();

  const fields = watch();

  const isDirty = !isEqual(fields, defaultFormValues) || caspAliasesList.length;

  const onDeleteCaspAlias = async (id: string) => {
    if (id.includes('casp-alias')) {
      setCaspAliasesList(caspAliasesList?.filter((item) => item.id !== id));
    } else {
      setSelectedCaspAliasId(id);

      await deleteCaspAlias(id);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');
    setCreationCaspAliasErrors([]);

    let requestIsAvailable = true;

    const updatedSettings: {
      [key: string]:
        | string
        | boolean
        | number
        | string[]
        | null
        | Record<string, string>
        | Record<string, boolean>;
      key: TransactionsSettingsKeys;
    }[] = [];

    if (
      values.exchangeCustomRateEnabled !==
      defaultFormValues?.exchangeCustomRateEnabled
    ) {
      updatedSettings.push({
        exchangeCustomRateEnabled: values.exchangeCustomRateEnabled as boolean,
        key: TransactionsSettingsKeys.EXCHANGE_CUSTOM_RATE_ENABLED,
      });
    }

    if (
      values.swiftMTCharSetValidation !==
      defaultFormValues?.swiftMTCharSetValidation
    ) {
      updatedSettings.push({
        swiftMTCharSetValidation: values.swiftMTCharSetValidation as boolean,
        key: TransactionsSettingsKeys.SWIFT_MT_CHAR_SET_VALIDATION,
      });
    }

    if (
      values.swiftISO20022CharSetValidation !==
      defaultFormValues?.swiftISO20022CharSetValidation
    ) {
      updatedSettings.push({
        swiftISO20022CharSetValidation:
          values.swiftISO20022CharSetValidation as boolean,
        key: TransactionsSettingsKeys.SWIFT_ISO20022_CHAR_SET_VALIDATION,
      });
    }

    if (values.addressMaxLength !== defaultFormValues?.addressMaxLength) {
      updatedSettings.push({
        addressMaxLength: Number(values.addressMaxLength),
        key: TransactionsSettingsKeys.ADDRESS_MAX_LENGTH,
      });
    }

    if (
      values.purposeOfPaymentMaxLength !==
      defaultFormValues?.purposeOfPaymentMaxLength
    ) {
      updatedSettings.push({
        purposeOfPaymentMaxLength: Number(values.purposeOfPaymentMaxLength),
        key: TransactionsSettingsKeys.PURPOSES_OF_PAYMENT_MAX_LENGTH,
      });
    }

    if (
      values.individualTransactionAttachDocumentsEnabled !==
      defaultFormValues?.individualTransactionAttachDocumentsEnabled
    ) {
      updatedSettings.push({
        transactionAttachDocumentsEnabled:
          values.individualTransactionAttachDocumentsEnabled as boolean,
        key: TransactionsSettingsKeys.INDIVIDUAL_TRANSACTION_ATTACH_DOCUMENTS_ENABLED,
      });
    }

    if (
      values.individualTransactionAttachDocumentsRequired !==
      defaultFormValues?.individualTransactionAttachDocumentsRequired
    ) {
      updatedSettings.push({
        transactionAttachDocumentsRequired:
          values.individualTransactionAttachDocumentsRequired as boolean,
        key: TransactionsSettingsKeys.INDIVIDUAL_TRANSACTION_ATTACH_DOCUMENTS_REQUIRED,
      });
    }

    if (
      values.corporateTransactionAttachDocumentsEnabled !==
      defaultFormValues?.corporateTransactionAttachDocumentsEnabled
    ) {
      updatedSettings.push({
        transactionAttachDocumentsEnabled:
          values.corporateTransactionAttachDocumentsEnabled as boolean,
        key: TransactionsSettingsKeys.CORPORATE_TRANSACTION_ATTACH_DOCUMENTS_ENABLED,
      });
    }

    if (
      values.corporateTransactionAttachDocumentsRequired !==
      defaultFormValues?.corporateTransactionAttachDocumentsRequired
    ) {
      updatedSettings.push({
        transactionAttachDocumentsRequired:
          values.corporateTransactionAttachDocumentsRequired as boolean,
        key: TransactionsSettingsKeys.CORPORATE_TRANSACTION_ATTACH_DOCUMENTS_REQUIRED,
      });
    }

    if (
      values.individualTransactionOutgoingPspPartnerEnabled !==
      defaultFormValues?.individualTransactionOutgoingPspPartnerEnabled
    ) {
      updatedSettings.push({
        customersAPIOutgoingPspPartnerEnabled:
          values.individualTransactionOutgoingPspPartnerEnabled as boolean,
        key: TransactionsSettingsKeys.INDIVIDUAL_CUSTOMERS_API_OUTGOING_PSP_PARTNER_ENABLED,
      });
    }

    if (
      values.individualTransactionOutgoingPspPartnerRequired !==
      defaultFormValues?.individualTransactionOutgoingPspPartnerRequired
    ) {
      updatedSettings.push({
        customersAPIOutgoingPspPartnerRequired:
          values.individualTransactionOutgoingPspPartnerRequired as boolean,
        key: TransactionsSettingsKeys.INDIVIDUAL_CUSTOMERS_API_OUTGOING_PSP_PARTNER_REQUIRED,
      });
    }

    if (
      values.corporateTransactionOutgoingPspPartnerEnabled !==
      defaultFormValues?.corporateTransactionOutgoingPspPartnerEnabled
    ) {
      updatedSettings.push({
        customersAPIOutgoingPspPartnerEnabled:
          values.corporateTransactionOutgoingPspPartnerEnabled as boolean,
        key: TransactionsSettingsKeys.CORPORATE_CUSTOMERS_API_OUTGOING_PSP_PARTNER_ENABLED,
      });
    }

    if (
      values.corporateTransactionOutgoingPspPartnerRequired !==
      defaultFormValues?.corporateTransactionOutgoingPspPartnerRequired
    ) {
      updatedSettings.push({
        customersAPIOutgoingPspPartnerRequired:
          values.corporateTransactionOutgoingPspPartnerRequired as boolean,
        key: TransactionsSettingsKeys.CORPORATE_CUSTOMERS_API_OUTGOING_PSP_PARTNER_REQUIRED,
      });
    }

    if (values.amlBotEnabled !== defaultFormValues?.amlBotEnabled) {
      updatedSettings.push({
        amlBotEnabled: values.amlBotEnabled as boolean,
        key: TransactionsSettingsKeys.AML_BOT_ENABLED,
      });
    }

    if (
      values.amlBotCurrencies &&
      !isEqual(values.amlBotCurrencies, defaultFormValues?.amlBotCurrencies)
    ) {
      updatedSettings.push({
        amlBotCurrencies: values.amlBotCurrencies,
        key: TransactionsSettingsKeys.AML_BOT_CURRENCIES,
      });
    }

    if (
      values.amlBotTransactionsTypes &&
      !isEqual(
        values.amlBotTransactionsTypes,
        defaultFormValues?.amlBotTransactionsTypes,
      )
    ) {
      updatedSettings.push({
        amlBotTransactionsTypes: values.amlBotTransactionsTypes,
        key: TransactionsSettingsKeys.AML_BOT_TRANSACTIONS_TYPES,
      });
    }

    if (
      values.beneficiariesAttachContractsEnabled !==
      defaultFormValues?.beneficiariesAttachContractsEnabled
    ) {
      updatedSettings.push({
        beneficiariesAttachContractsEnabled:
          values.beneficiariesAttachContractsEnabled as boolean,
        key: TransactionsSettingsKeys.CORPORATE_BENEFICIARIES_ATTACH_CONTRACTS_ENABLED,
      });
    }

    if (
      values.beneficiariesContractsRequiredEnabled !==
      defaultFormValues?.beneficiariesContractsRequiredEnabled
    ) {
      updatedSettings.push({
        beneficiariesContractsRequiredEnabled:
          values.beneficiariesContractsRequiredEnabled as boolean,
        key: TransactionsSettingsKeys.CORPORATE_BENEFICIARIES_CONTRACTS_REQUIRED_ENABLED,
      });
    }

    if (
      values.amlBotAddressAutoresolveEnable !==
      defaultFormValues?.amlBotAddressAutoresolveEnable
    ) {
      updatedSettings.push({
        amlBotAddressAutoresolveEnable:
          values.amlBotAddressAutoresolveEnable as boolean,
        key: TransactionsSettingsKeys.AML_BOT_ADDRESS_AUTORESOLVE_ENABLE,
      });
    }

    if (values.cryptoSwiftEnabled !== defaultFormValues?.cryptoSwiftEnabled) {
      updatedSettings.push({
        cryptoSwiftEnabled: values.cryptoSwiftEnabled as boolean,
        key: TransactionsSettingsKeys.CRYPTOSWIFT_ENABLED,
      });
    }

    if (
      values.amlBotAddressAutoresolveEnable &&
      !values.amlBotAddressAutoresolveValue
    ) {
      requestIsAvailable = false;
      setError('amlBotAddressAutoresolveValue', {
        type: 'custom',
        message: 'Risk score is required',
      });
    }

    if (
      values.amlBotAddressAutoresolveValue !==
      defaultFormValues?.amlBotAddressAutoresolveValue
    ) {
      updatedSettings.push({
        amlBotAddressAutoresolveValue: values.amlBotAddressAutoresolveValue
          ? Number(values.amlBotAddressAutoresolveValue)
          : null,
        key: TransactionsSettingsKeys.AML_BOT_ADDRESS_AUTORESOLVE_VALUE,
      });
    }

    if (
      values.amlBotAddressAutodeclineEnable !==
      defaultFormValues?.amlBotAddressAutodeclineEnable
    ) {
      updatedSettings.push({
        amlBotAddressAutodeclineEnable:
          values.amlBotAddressAutodeclineEnable as boolean,
        key: TransactionsSettingsKeys.AML_BOT_ADDRESS_AUTODECLINE_ENABLE,
      });
    }

    if (
      values.amlBotAddressAutodeclineEnable &&
      !values.amlBotAddressAutodeclineValue
    ) {
      requestIsAvailable = false;
      setError('amlBotAddressAutodeclineValue', {
        type: 'custom',
        message: 'Risk score is required',
      });
    }

    if (
      values.amlBotAddressAutodeclineValue !==
      defaultFormValues?.amlBotAddressAutodeclineValue
    ) {
      updatedSettings.push({
        amlBotAddressAutodeclineValue: values.amlBotAddressAutodeclineValue
          ? Number(values.amlBotAddressAutodeclineValue)
          : null,
        key: TransactionsSettingsKeys.AML_BOT_ADDRESS_AUTODECLINE_VALUE,
      });
    }

    if (
      values.amlBotIncomingTxAutoresolveEnable !==
      defaultFormValues?.amlBotIncomingTxAutoresolveEnable
    ) {
      updatedSettings.push({
        amlBotIncomingTxAutoresolveEnable:
          values.amlBotIncomingTxAutoresolveEnable as boolean,
        key: TransactionsSettingsKeys.AML_BOT_INCOMING_TX_AUTORESOLVE_ENABLE,
      });
    }

    if (
      values.amlBotIncomingTxAutoresolveEnable &&
      !values.amlBotIncomingTxAutoresolveValue
    ) {
      requestIsAvailable = false;
      setError('amlBotIncomingTxAutoresolveValue', {
        type: 'custom',
        message: 'Risk score is required',
      });
    }

    if (
      values.amlBotIncomingTxAutoresolveValue !==
      defaultFormValues?.amlBotIncomingTxAutoresolveValue
    ) {
      updatedSettings.push({
        amlBotIncomingTxAutoresolveValue:
          values.amlBotIncomingTxAutoresolveValue
            ? Number(values.amlBotIncomingTxAutoresolveValue)
            : null,
        key: TransactionsSettingsKeys.AML_BOT_INCOMING_TX_AUTORESOLVE_VALUE,
      });
    }

    if (
      values.amlBotIncomingTxAutodeclineEnable !==
      defaultFormValues?.amlBotIncomingTxAutodeclineEnable
    ) {
      updatedSettings.push({
        amlBotIncomingTxAutodeclineEnable:
          values.amlBotIncomingTxAutodeclineEnable as boolean,
        key: TransactionsSettingsKeys.AML_BOT_INCOMING_TX_AUTODECLINE_ENABLE,
      });
    }

    if (
      values.amlBotIncomingTxAutodeclineEnable &&
      !values.amlBotIncomingTxAutodeclineValue
    ) {
      requestIsAvailable = false;
      setError('amlBotIncomingTxAutodeclineValue', {
        type: 'custom',
        message: 'Risk score is required',
      });
    }

    if (
      values.amlBotIncomingTxAutodeclineValue !==
      defaultFormValues?.amlBotIncomingTxAutodeclineValue
    ) {
      updatedSettings.push({
        amlBotIncomingTxAutodeclineValue:
          values.amlBotIncomingTxAutodeclineValue
            ? Number(values.amlBotIncomingTxAutodeclineValue)
            : null,
        key: TransactionsSettingsKeys.AML_BOT_INCOMING_TX_AUTODECLINE_VALUE,
      });
    }

    if (updatedSettings.length > 0 && requestIsAvailable) {
      const response = (await Promise.all(
        updatedSettings.map(async (item) => {
          const res = await edit(item);

          return res;
        }),
      )) as { error?: ResponseError }[];

      if (response.find((item) => item.error)) {
        const errorsResponse = response.filter((item) => item.error);

        errorsResponse.forEach((item) => {
          responseErrorsResolver({
            errors: item.error as ResponseError,
            setError,
            setGlobalError,
          });
        });
      }

      const body: BeneficiariesSettingsVormValues = {
        individualTransactionAttachDocumentsEnabled:
          values.individualTransactionAttachDocumentsEnabled,
        individualTransactionAttachDocumentsRequired:
          values.individualTransactionAttachDocumentsRequired,
        corporateTransactionAttachDocumentsEnabled:
          values.corporateTransactionAttachDocumentsEnabled,
        corporateTransactionAttachDocumentsRequired:
          values.corporateTransactionAttachDocumentsRequired,
        individualTransactionOutgoingPspPartnerEnabled:
          values.individualTransactionOutgoingPspPartnerEnabled,
        individualTransactionOutgoingPspPartnerRequired:
          values.individualTransactionOutgoingPspPartnerRequired,
        corporateTransactionOutgoingPspPartnerEnabled:
          values.corporateTransactionOutgoingPspPartnerEnabled,
        corporateTransactionOutgoingPspPartnerRequired:
          values.corporateTransactionOutgoingPspPartnerRequired,
        amlBotEnabled: values.amlBotEnabled,
        amlBotTransactionsTypes: values.amlBotTransactionsTypes,
        amlBotCurrencies: values.amlBotCurrencies,
        beneficiariesAttachContractsEnabled:
          values.beneficiariesAttachContractsEnabled,
        beneficiariesContractsRequiredEnabled:
          values.beneficiariesContractsRequiredEnabled,
        exchangeCustomRateEnabled: values.exchangeCustomRateEnabled,
        addressMaxLength: Number(values.addressMaxLength),
        purposeOfPaymentMaxLength: Number(values.purposeOfPaymentMaxLength),
        swiftISO20022CharSetValidation: values.swiftISO20022CharSetValidation,
        swiftMTCharSetValidation: values.swiftMTCharSetValidation,
        amlBotAddressAutoresolveEnable: values.amlBotAddressAutoresolveEnable,
        amlBotAddressAutoresolveValue:
          values.amlBotAddressAutoresolveValue || '',
        amlBotAddressAutodeclineEnable: values.amlBotAddressAutodeclineEnable,
        amlBotAddressAutodeclineValue:
          values.amlBotAddressAutodeclineValue || '',
        amlBotIncomingTxAutoresolveEnable:
          values.amlBotIncomingTxAutoresolveEnable,
        amlBotIncomingTxAutoresolveValue:
          values.amlBotIncomingTxAutoresolveValue || '',
        amlBotIncomingTxAutodeclineEnable:
          values.amlBotIncomingTxAutodeclineEnable,
        amlBotIncomingTxAutodeclineValue:
          values.amlBotIncomingTxAutodeclineValue || '',
        cryptoSwiftEnabled: values.cryptoSwiftEnabled,
      };

      setDefaultFormValues(body);
    }

    if (caspAliasesList?.length) {
      const nextCaspAliasCreationErrors: {
        id: string;
        error: ResponseError;
      }[] = [];

      let nextCaspAliases: CASPAlias[] = [];
      let nextCaspAlisesList = ([] as CASPAliasFormValues[]).concat(
        caspAliasesList,
      );

      if (caspAliases?.length) {
        nextCaspAliases = ([] as CASPAlias[]).concat(caspAliases);
      }

      const responses = await Promise.all(
        caspAliasesList?.map(async (item) => {
          const response = await createCaspAliases({
            caspId: item.caspId,
            alias: item.alias,
            source: item.source,
          });

          if (response) {
            if (!response.error) {
              nextCaspAliases.push({
                caspId: response?.data?.data.caspId,
                alias: response?.data?.data.alias,
                source: response?.data?.data.source,
                id: response?.data?.data.id,
              });

              nextCaspAlisesList = nextCaspAlisesList?.filter(
                (caspAlias) => caspAlias.id !== item.id,
              );
            } else if (response.error) {
              nextCaspAlisesList?.forEach((caspAlias) => {
                if (item.id === caspAlias.id) {
                  nextCaspAliasCreationErrors.push({
                    id: caspAlias.id as string,
                    error: response.error as ResponseError,
                  });
                }
              });
            }
          }

          return response;
        }),
      );

      if (responses) {
        setCaspAliasesList(nextCaspAlisesList);
        dispatch(setCaspAliases(nextCaspAliases));
      }

      if (nextCaspAliasCreationErrors?.length) {
        setCreationCaspAliasErrors(nextCaspAliasCreationErrors);
      }
    }
  });

  const getBeneficiariesSettingsAction = useCallback(async () => {
    const response = (await getBeneficiariesSettings()) as {
      error?: ResponseError;
    };

    if (response?.error) {
      getResponseErrors({ error: response?.error, setErrorList });
    }
  }, []);

  useEffect(() => {
    if (!pspDetails) {
      getBeneficiariesSettingsAction();
    }
  }, [pspDetails]);

  useEffect(() => {
    if (activePermissions?.includes(PermissionEnum.CASP_ALIAS_LIST)) {
      getCASPAliases();
      getAMLBotAssets();
    }
  }, [activePermissions]);

  useEffect(() => {
    if (deleteCaspAliasIsSuccess) {
      setSelectedCaspAliasId('');
    }
  }, [deleteCaspAliasIsSuccess]);

  useEffect(() => {
    if (pspDetails) {
      const individualTransactionAttachDocumentsEnabled = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.INDIVIDUAL_TRANSACTION_ATTACH_DOCUMENTS_ENABLED,
      )?.data?.value as boolean;

      const individualTransactionAttachDocumentsRequired = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.INDIVIDUAL_TRANSACTION_ATTACH_DOCUMENTS_REQUIRED,
      )?.data?.value as boolean;

      const corporateTransactionAttachDocumentsEnabled = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.CORPORATE_TRANSACTION_ATTACH_DOCUMENTS_ENABLED,
      )?.data?.value as boolean;

      const corporateTransactionAttachDocumentsRequired = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.CORPORATE_TRANSACTION_ATTACH_DOCUMENTS_REQUIRED,
      )?.data?.value as boolean;

      const individualTransactionOutgoingPspPartnerEnabled = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.INDIVIDUAL_CUSTOMERS_API_OUTGOING_PSP_PARTNER_ENABLED,
      )?.data?.value as boolean;

      const individualTransactionOutgoingPspPartnerRequired = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.INDIVIDUAL_CUSTOMERS_API_OUTGOING_PSP_PARTNER_REQUIRED,
      )?.data?.value as boolean;

      const corporateTransactionOutgoingPspPartnerEnabled = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.CORPORATE_CUSTOMERS_API_OUTGOING_PSP_PARTNER_ENABLED,
      )?.data?.value as boolean;

      const corporateTransactionOutgoingPspPartnerRequired = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.CORPORATE_CUSTOMERS_API_OUTGOING_PSP_PARTNER_REQUIRED,
      )?.data?.value as boolean;

      const amlBotEnabled = pspDetails?.find(
        (item) => item.key === TransactionsSettingsKeys.AML_BOT_ENABLED,
      )?.data?.value as boolean;

      const amlBotCurrencies = pspDetails?.find(
        (item) => item.key === TransactionsSettingsKeys.AML_BOT_CURRENCIES,
      )?.data?.value as string[];

      const amlBotTransactionsTypes = pspDetails?.find(
        (item) =>
          item.key === TransactionsSettingsKeys.AML_BOT_TRANSACTIONS_TYPES,
      )?.data?.value as string[];

      const beneficiariesAttachContractsEnabled = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.CORPORATE_BENEFICIARIES_ATTACH_CONTRACTS_ENABLED,
      )?.data?.value as boolean;

      const beneficiariesContractsRequiredEnabled = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.CORPORATE_BENEFICIARIES_CONTRACTS_REQUIRED_ENABLED,
      )?.data?.value as boolean;

      const exchangeCustomRateEnabled = pspDetails?.find(
        (item) =>
          item.key === TransactionsSettingsKeys.EXCHANGE_CUSTOM_RATE_ENABLED,
      )?.data?.value as boolean;

      const addressMaxLength = pspDetails?.find(
        (item) => item.key === TransactionsSettingsKeys.ADDRESS_MAX_LENGTH,
      )?.data?.value as string;

      const purposeOfPaymentMaxLength = pspDetails?.find(
        (item) =>
          item.key === TransactionsSettingsKeys.PURPOSES_OF_PAYMENT_MAX_LENGTH,
      )?.data?.value as string;

      const swiftMTCharSetValidation = pspDetails?.find(
        (item) =>
          item.key === TransactionsSettingsKeys.SWIFT_MT_CHAR_SET_VALIDATION,
      )?.data?.value as boolean;

      const swiftISO20022CharSetValidation = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.SWIFT_ISO20022_CHAR_SET_VALIDATION,
      )?.data?.value as boolean;

      const amlBotAddressAutoresolveEnable = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.AML_BOT_ADDRESS_AUTORESOLVE_ENABLE,
      )?.data.value as boolean;
      const amlBotAddressAutoresolveValue = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.AML_BOT_ADDRESS_AUTORESOLVE_VALUE,
      )?.data.value as string;
      const amlBotAddressAutodeclineEnable = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.AML_BOT_ADDRESS_AUTODECLINE_ENABLE,
      )?.data.value as boolean;
      const amlBotAddressAutodeclineValue = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.AML_BOT_ADDRESS_AUTODECLINE_VALUE,
      )?.data.value as string;
      const amlBotIncomingTxAutoresolveEnable = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.AML_BOT_INCOMING_TX_AUTORESOLVE_ENABLE,
      )?.data.value as boolean;
      const amlBotIncomingTxAutoresolveValue = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.AML_BOT_INCOMING_TX_AUTORESOLVE_VALUE,
      )?.data.value as string;
      const amlBotIncomingTxAutodeclineEnable = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.AML_BOT_INCOMING_TX_AUTODECLINE_ENABLE,
      )?.data.value as boolean;
      const amlBotIncomingTxAutodeclineValue = pspDetails?.find(
        (item) =>
          item.key ===
          TransactionsSettingsKeys.AML_BOT_INCOMING_TX_AUTODECLINE_VALUE,
      )?.data.value as string;
      const cryptoSwiftEnabled = pspDetails?.find(
        (item) => item.key === TransactionsSettingsKeys.CRYPTOSWIFT_ENABLED,
      )?.data.value as boolean;

      const body: BeneficiariesSettingsVormValues = {
        individualTransactionAttachDocumentsEnabled,
        individualTransactionAttachDocumentsRequired,
        corporateTransactionAttachDocumentsEnabled,
        corporateTransactionAttachDocumentsRequired,
        individualTransactionOutgoingPspPartnerEnabled,
        individualTransactionOutgoingPspPartnerRequired,
        corporateTransactionOutgoingPspPartnerEnabled,
        corporateTransactionOutgoingPspPartnerRequired,
        amlBotEnabled,
        amlBotCurrencies,
        amlBotTransactionsTypes,
        beneficiariesAttachContractsEnabled,
        beneficiariesContractsRequiredEnabled,
        exchangeCustomRateEnabled,
        addressMaxLength,
        purposeOfPaymentMaxLength,
        swiftISO20022CharSetValidation,
        swiftMTCharSetValidation,
        amlBotAddressAutoresolveEnable,
        amlBotAddressAutoresolveValue: amlBotAddressAutoresolveValue
          ? amlBotAddressAutoresolveValue.toString()
          : '',
        amlBotAddressAutodeclineEnable,
        amlBotAddressAutodeclineValue: amlBotAddressAutodeclineValue
          ? amlBotAddressAutodeclineValue.toString()
          : '',
        amlBotIncomingTxAutoresolveEnable,
        amlBotIncomingTxAutoresolveValue: amlBotIncomingTxAutoresolveValue
          ? amlBotIncomingTxAutoresolveValue.toString()
          : '',
        amlBotIncomingTxAutodeclineEnable,
        amlBotIncomingTxAutodeclineValue: amlBotIncomingTxAutodeclineValue
          ? amlBotIncomingTxAutodeclineValue.toString()
          : '',
        cryptoSwiftEnabled,
      };

      reset(body);
      setDefaultFormValues(body);
    }
  }, [pspDetails]);

  useEffect(() => {
    if (!transactionsTypes) {
      getTransactionTypesList();
    }

    if (!activeCurrencies) {
      getActiveCurrencies();
    }
  }, [transactionsTypes, activeCurrencies]);

  useEffect(() => {
    if (!casp && activePermissions?.includes(PermissionEnum.CASP_LIST)) {
      getCaspLists();
    }
  }, [casp, activePermissions]);

  useEffect(() => {
    let title = 'Transaction Settings: Options';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;

  if (
    isLoading ||
    isFetching ||
    transactionTypesIsLoading ||
    activeCurrenciesIsLoading
  ) {
    content = (
      <div className={cn.beneficiariesSettingsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <div className={cn.beneficiariesSettingsContent}>
        {!errorList && (
          <>
            <div className={cn.beneficiariesSettingsBlock}>
              <div className={cn.beneficiariesSettingsTitle}>
                Purpose of Payment
              </div>
              <div className={cn.formGroup}>
                <Controller
                  control={control}
                  name="purposeOfPaymentMaxLength"
                  render={({ field: { value, name, onChange } }) => (
                    <>
                      <InputNumber
                        name={name}
                        value={value || undefined}
                        onChange={(e) => {
                          if (Number(e.target.value) > 140) {
                            onChange('140');
                          } else {
                            onChange(e);
                          }
                        }}
                        title="Max. number of characters for the purpose of payment"
                        placeholder="Enter number"
                        labelClassName={cn.limitLabel}
                      />
                      <div className={cn.optionLimit}>
                        The default limit is 140 characters
                      </div>
                    </>
                  )}
                />
              </div>
              <div className={cn.formGroup}>
                <Controller
                  control={control}
                  name="addressMaxLength"
                  render={({ field: { value, name, onChange } }) => (
                    <>
                      <InputText
                        name={name}
                        value={value || undefined}
                        onChange={onChange}
                        title="Max. number of characters for the address"
                        placeholder="Enter number"
                        labelClassName={cn.limitLabel}
                      />
                      <div className={cn.optionLimit}>No limit by default</div>
                    </>
                  )}
                />
              </div>
            </div>
            <div className={cn.beneficiariesSettingsBlock}>
              <div className={cn.beneficiariesSettingsTitle}>
                Transactions validations
              </div>
              <div className={cn.formGroup}>
                <Controller
                  control={control}
                  name="swiftMTCharSetValidation"
                  render={({ field: { value, name, onChange } }) => (
                    <>
                      <ToggleInput
                        name={name}
                        title="SWIFT MT Char Set Validation"
                        onChange={(e) => {
                          onChange(e.target.checked);
                        }}
                        checked={value}
                      />
                      {errors?.swiftMTCharSetValidation?.message && (
                        <div className={cn.settingError}>
                          {errors?.swiftMTCharSetValidation?.message}
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
              <div className={cn.formGroup}>
                <Controller
                  control={control}
                  name="swiftISO20022CharSetValidation"
                  render={({ field: { value, name, onChange } }) => (
                    <>
                      <ToggleInput
                        name={name}
                        title="SWIFT ISO20022 Char Set Validation"
                        onChange={(e) => {
                          onChange(e.target.checked);
                        }}
                        checked={value}
                      />
                      {errors?.swiftISO20022CharSetValidation?.message && (
                        <div className={cn.settingError}>
                          {errors?.swiftISO20022CharSetValidation?.message}
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
            <div className={cn.beneficiariesSettingsBlock}>
              <div className={cn.beneficiariesSettingsTitle}>Change bank</div>
              <div className={cn.transactionOptionsTitle}>
                Individual customers
              </div>
              <div className={cn.transactionOptionsBlock}>
                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    name="individualTransactionOutgoingPspPartnerEnabled"
                    render={({ field: { value, name, onChange } }) => (
                      <>
                        <ToggleInput
                          name={name}
                          title="Choosing a bank for an outgoing transaction"
                          onChange={(e) => {
                            onChange(e.target.checked);

                            if (!e.target.checked) {
                              setValue(
                                'individualTransactionOutgoingPspPartnerRequired',
                                false,
                              );
                            }
                          }}
                          checked={value}
                        />
                        {errors?.individualTransactionOutgoingPspPartnerEnabled
                          ?.message && (
                          <div className={cn.settingError}>
                            {
                              errors
                                ?.individualTransactionOutgoingPspPartnerEnabled
                                ?.message
                            }
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    name="individualTransactionOutgoingPspPartnerRequired"
                    render={({ field: { value, name, onChange } }) => (
                      <>
                        <ToggleInput
                          name={name}
                          title="Choosing a bank is required"
                          onChange={(e) => onChange(e.target.checked)}
                          checked={value}
                          disabled={
                            !fields.individualTransactionOutgoingPspPartnerEnabled
                          }
                        />
                        {errors?.individualTransactionOutgoingPspPartnerRequired
                          ?.message && (
                          <div className={cn.settingError}>
                            {
                              errors
                                ?.individualTransactionOutgoingPspPartnerRequired
                                ?.message
                            }
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
              </div>
              <div className={cn.transactionOptionsTitle}>
                Corporate customers
              </div>
              <div className={cn.transactionOptionsBlock}>
                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    name="corporateTransactionOutgoingPspPartnerEnabled"
                    render={({ field: { value, name, onChange } }) => (
                      <>
                        <ToggleInput
                          name={name}
                          title="Choosing a bank for an outgoing transaction"
                          onChange={(e) => {
                            onChange(e.target.checked);

                            if (!e.target.checked) {
                              setValue(
                                'corporateTransactionOutgoingPspPartnerRequired',
                                false,
                              );
                            }
                          }}
                          checked={value}
                        />
                        {errors?.corporateTransactionOutgoingPspPartnerEnabled
                          ?.message && (
                          <div className={cn.settingError}>
                            {
                              errors
                                ?.corporateTransactionOutgoingPspPartnerEnabled
                                ?.message
                            }
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    name="corporateTransactionOutgoingPspPartnerRequired"
                    render={({ field: { value, name, onChange } }) => (
                      <>
                        <ToggleInput
                          name={name}
                          title="Choosing a bank is required"
                          onChange={(e) => onChange(e.target.checked)}
                          checked={value}
                          disabled={
                            !fields.corporateTransactionOutgoingPspPartnerEnabled
                          }
                        />
                        {errors?.corporateTransactionOutgoingPspPartnerRequired
                          ?.message && (
                          <div className={cn.settingError}>
                            {
                              errors
                                ?.corporateTransactionOutgoingPspPartnerRequired
                                ?.message
                            }
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className={cn.beneficiariesSettingsBlock}>
              <div className={cn.beneficiariesSettingsTitle}>
                Attaching documents to a transaction
              </div>
              <div className={cn.transactionOptionsBlock}>
                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    name="beneficiariesAttachContractsEnabled"
                    render={({ field: { value, name, onChange } }) => (
                      <>
                        <ToggleInput
                          name={name}
                          title="Attaching contracts to transactions"
                          onChange={(e) => {
                            onChange(e.target.checked);

                            if (!e.target.checked) {
                              setValue(
                                'beneficiariesContractsRequiredEnabled',
                                false,
                              );
                            }
                          }}
                          checked={value}
                        />
                        {errors?.beneficiariesAttachContractsEnabled
                          ?.message && (
                          <div className={cn.settingError}>
                            {
                              errors?.beneficiariesAttachContractsEnabled
                                ?.message
                            }
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>

                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    name="beneficiariesContractsRequiredEnabled"
                    render={({ field: { value, name, onChange } }) => (
                      <>
                        <ToggleInput
                          name={name}
                          title="Attaching contracts required"
                          onChange={(e) => onChange(e.target.checked)}
                          checked={value}
                          disabled={!fields.beneficiariesAttachContractsEnabled}
                        />
                        {errors?.beneficiariesContractsRequiredEnabled
                          ?.message && (
                          <div className={cn.settingError}>
                            {
                              errors?.beneficiariesContractsRequiredEnabled
                                ?.message
                            }
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
              </div>
              <div className={cn.transactionOptionsTitle}>
                Individual customers
              </div>
              <div className={cn.transactionOptionsBlock}>
                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    name="individualTransactionAttachDocumentsEnabled"
                    render={({ field: { value, name, onChange } }) => (
                      <>
                        <ToggleInput
                          name={name}
                          title="Attaching documents to transactions"
                          onChange={(e) => {
                            onChange(e.target.checked);

                            if (!e.target.checked) {
                              setValue(
                                'individualTransactionAttachDocumentsRequired',
                                false,
                              );
                            }
                          }}
                          checked={value}
                        />
                        {errors?.individualTransactionAttachDocumentsEnabled
                          ?.message && (
                          <div className={cn.settingError}>
                            {
                              errors
                                ?.individualTransactionAttachDocumentsEnabled
                                ?.message
                            }
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    name="individualTransactionAttachDocumentsRequired"
                    render={({ field: { value, name, onChange } }) => (
                      <>
                        <ToggleInput
                          name={name}
                          title="Attaching documents is required"
                          onChange={(e) => onChange(e.target.checked)}
                          checked={value}
                          disabled={
                            !fields.individualTransactionAttachDocumentsEnabled
                          }
                        />
                        {errors?.individualTransactionAttachDocumentsRequired
                          ?.message && (
                          <div className={cn.settingError}>
                            {
                              errors
                                ?.individualTransactionAttachDocumentsRequired
                                ?.message
                            }
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
              </div>
              <div className={cn.transactionOptionsTitle}>
                Corporate customers
              </div>
              <div className={cn.transactionOptionsBlock}>
                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    name="corporateTransactionAttachDocumentsEnabled"
                    render={({ field: { value, name, onChange } }) => (
                      <>
                        <ToggleInput
                          name={name}
                          title="Attaching documents to transactions"
                          onChange={(e) => {
                            onChange(e.target.checked);

                            if (!e.target.checked) {
                              setValue(
                                'corporateTransactionAttachDocumentsRequired',
                                false,
                              );
                            }
                          }}
                          checked={value}
                        />
                        {errors?.corporateTransactionAttachDocumentsEnabled
                          ?.message && (
                          <div className={cn.settingError}>
                            {
                              errors?.corporateTransactionAttachDocumentsEnabled
                                ?.message
                            }
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    name="corporateTransactionAttachDocumentsRequired"
                    render={({ field: { value, name, onChange } }) => (
                      <>
                        <ToggleInput
                          name={name}
                          title="Attaching documents is required"
                          onChange={(e) => onChange(e.target.checked)}
                          checked={value}
                          disabled={
                            !fields.corporateTransactionAttachDocumentsEnabled
                          }
                        />
                        {errors?.corporateTransactionAttachDocumentsRequired
                          ?.message && (
                          <div className={cn.settingError}>
                            {
                              errors
                                ?.corporateTransactionAttachDocumentsRequired
                                ?.message
                            }
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className={cn.beneficiariesSettingsBlock}>
              <div className={cn.beneficiariesSettingsTitle}>
                FX Rate request
              </div>
              <div className={`${cn.formGroup} ${cn.requiredFromGroup}`}>
                <Controller
                  control={control}
                  name="exchangeCustomRateEnabled"
                  render={({ field: { value, name, onChange } }) => (
                    <>
                      <ToggleInput
                        name={name}
                        title="Enable FX Rate request"
                        onChange={(e) => onChange(e.target.checked)}
                        checked={value}
                      />
                      {errors?.exchangeCustomRateEnabled?.message && (
                        <div className={cn.settingError}>
                          {errors?.exchangeCustomRateEnabled?.message}
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
            <div className={cn.beneficiariesSettingsBlock}>
              <div className={cn.beneficiariesSettingsTitle}>
                AML Bot Settings
              </div>
              <div className={cn.amlBotSettingsWrapper}>
                <Controller
                  control={control}
                  name="amlBotEnabled"
                  render={({ field: { value, name, onChange } }) => (
                    <>
                      <ToggleInput
                        name={name}
                        title="Required"
                        onChange={(e) => {
                          onChange(e.target.checked);

                          if (!e.target.checked) {
                            setValue('amlBotAddressAutoresolveEnable', false);
                            setValue('amlBotAddressAutoresolveValue', null);
                            setValue('amlBotAddressAutodeclineEnable', false);
                            setValue('amlBotAddressAutodeclineValue', null);
                            setValue(
                              'amlBotIncomingTxAutoresolveEnable',
                              false,
                            );
                            setValue('amlBotIncomingTxAutoresolveValue', null);
                            setValue(
                              'amlBotIncomingTxAutodeclineEnable',
                              false,
                            );
                            setValue('amlBotIncomingTxAutodeclineValue', null);
                          }
                        }}
                        checked={value}
                      />
                      {errors?.amlBotEnabled?.message && (
                        <div className={cn.settingError}>
                          {errors?.amlBotEnabled?.message}
                        </div>
                      )}
                    </>
                  )}
                />
                {fields?.amlBotEnabled && (
                  <div className={cn.amlBotAdditionalSettings}>
                    <div className={cn.amlBotAdditionalSettingsHint}>
                      <IconInfo width={16} height={16} />
                      Autodecline threshold should be higher than Autoresolve
                      threshold
                    </div>
                    <div className={cn.amlBotAdditionalSettingsHint}>
                      Verifying the sender’s wallet address for an incoming
                      transaction and the recipient’s wallet address for an
                      outgoing transaction
                    </div>
                    <div className={cn.formGroup}>
                      <Controller
                        control={control}
                        name="amlBotAddressAutoresolveEnable"
                        render={({ field: { value, name, onChange } }) => (
                          <>
                            <ToggleInput
                              name={name}
                              title="Address verification warning auto resolution"
                              onChange={(e) => {
                                onChange(e.target.checked);

                                if (
                                  fields.amlBotAddressAutoresolveValue &&
                                  !e.target.checked
                                ) {
                                  setValue('amlBotAddressAutoresolveValue', '');
                                }

                                if (
                                  errors?.amlBotAddressAutoresolveValue
                                    ?.message &&
                                  !e.target.checked
                                ) {
                                  clearErrors([
                                    'amlBotAddressAutoresolveValue',
                                  ]);
                                }
                              }}
                              checked={value}
                            />
                            {errors?.amlBotAddressAutoresolveEnable
                              ?.message && (
                              <div className={cn.settingError}>
                                {
                                  errors?.amlBotAddressAutoresolveEnable
                                    ?.message
                                }
                              </div>
                            )}
                          </>
                        )}
                      />
                      {fields?.amlBotAddressAutoresolveEnable && (
                        <div
                          className={
                            cn.amlBotAdditionalSettingsAdditionalWrapper
                          }
                        >
                          <Controller
                            control={control}
                            name="amlBotAddressAutoresolveValue"
                            render={({ field: { value, name, onChange } }) => (
                              <InputText
                                size={InputSize.SMALL}
                                name={name}
                                value={value as string}
                                placeholder="Enter risk score from 0 to 100"
                                error={
                                  errors?.amlBotAddressAutoresolveValue?.message
                                }
                                onChange={(e) => {
                                  if (
                                    errors?.amlBotAddressAutoresolveValue
                                      ?.message
                                  ) {
                                    clearErrors([
                                      'amlBotAddressAutoresolveValue',
                                    ]);
                                  }

                                  if (Number(e.target.value) > 100) {
                                    onChange('100');
                                  } else if (Number(e.target.value) < 0) {
                                    onChange('0');
                                  } else {
                                    onChange(e);
                                  }
                                }}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                    <div className={cn.formGroup}>
                      <Controller
                        control={control}
                        name="amlBotAddressAutodeclineEnable"
                        render={({ field: { value, name, onChange } }) => (
                          <>
                            <ToggleInput
                              name={name}
                              title="Address verification warning auto decline"
                              onChange={(e) => {
                                onChange(e.target.checked);

                                if (
                                  fields.amlBotAddressAutodeclineValue &&
                                  !e.target.checked
                                ) {
                                  setValue('amlBotAddressAutodeclineValue', '');
                                }

                                if (
                                  errors?.amlBotAddressAutodeclineValue
                                    ?.message &&
                                  !e.target.checked
                                ) {
                                  clearErrors([
                                    'amlBotAddressAutodeclineValue',
                                  ]);
                                }
                              }}
                              checked={value}
                            />
                            {errors?.amlBotAddressAutodeclineEnable
                              ?.message && (
                              <div className={cn.settingError}>
                                {
                                  errors?.amlBotAddressAutodeclineEnable
                                    ?.message
                                }
                              </div>
                            )}
                          </>
                        )}
                      />
                      {fields?.amlBotAddressAutodeclineEnable && (
                        <div
                          className={
                            cn.amlBotAdditionalSettingsAdditionalWrapper
                          }
                        >
                          <Controller
                            control={control}
                            name="amlBotAddressAutodeclineValue"
                            render={({ field: { value, name, onChange } }) => (
                              <InputText
                                size={InputSize.SMALL}
                                name={name}
                                value={value as string}
                                placeholder="Enter risk score from 0 to 100"
                                error={
                                  errors?.amlBotAddressAutodeclineValue?.message
                                }
                                onChange={(e) => {
                                  if (
                                    errors?.amlBotAddressAutodeclineValue
                                      ?.message
                                  ) {
                                    clearErrors([
                                      'amlBotAddressAutodeclineValue',
                                    ]);
                                  }

                                  if (Number(e.target.value) > 100) {
                                    onChange('100');
                                  } else if (Number(e.target.value) < 0) {
                                    onChange('0');
                                  } else {
                                    onChange(e);
                                  }
                                }}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                    <div className={cn.amlBotSettingsSeparator} />
                    <div
                      className={`${cn.amlBotAdditionalSettingsHint} ${cn.withMargin}`}
                    >
                      Blockchain transaction verification for incoming
                      transactions
                    </div>
                    <div className={cn.formGroup}>
                      <Controller
                        control={control}
                        name="amlBotIncomingTxAutoresolveEnable"
                        render={({ field: { value, name, onChange } }) => (
                          <>
                            <ToggleInput
                              name={name}
                              title="Blockchain transaction verification warning auto resolution"
                              onChange={(e) => {
                                onChange(e.target.checked);

                                if (
                                  fields.amlBotIncomingTxAutoresolveValue &&
                                  !e.target.checked
                                ) {
                                  setValue(
                                    'amlBotIncomingTxAutoresolveValue',
                                    '',
                                  );
                                }

                                if (
                                  errors?.amlBotIncomingTxAutoresolveValue
                                    ?.message &&
                                  !e.target.checked
                                ) {
                                  clearErrors([
                                    'amlBotIncomingTxAutoresolveValue',
                                  ]);
                                }
                              }}
                              checked={value}
                            />
                            {errors?.amlBotIncomingTxAutoresolveEnable
                              ?.message && (
                              <div className={cn.settingError}>
                                {
                                  errors?.amlBotIncomingTxAutoresolveEnable
                                    ?.message
                                }
                              </div>
                            )}
                          </>
                        )}
                      />
                      {fields?.amlBotIncomingTxAutoresolveEnable && (
                        <div
                          className={
                            cn.amlBotAdditionalSettingsAdditionalWrapper
                          }
                        >
                          <Controller
                            control={control}
                            name="amlBotIncomingTxAutoresolveValue"
                            render={({ field: { value, name, onChange } }) => (
                              <InputText
                                size={InputSize.SMALL}
                                name={name}
                                value={value as string}
                                placeholder="Enter risk score from 0 to 100"
                                error={
                                  errors?.amlBotIncomingTxAutoresolveValue
                                    ?.message
                                }
                                onChange={(e) => {
                                  if (
                                    errors?.amlBotIncomingTxAutoresolveValue
                                      ?.message
                                  ) {
                                    clearErrors([
                                      'amlBotIncomingTxAutoresolveValue',
                                    ]);
                                  }

                                  if (Number(e.target.value) > 100) {
                                    onChange('100');
                                  } else if (Number(e.target.value) < 0) {
                                    onChange('0');
                                  } else {
                                    onChange(e);
                                  }
                                }}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                    <div className={cn.formGroup}>
                      <Controller
                        control={control}
                        name="amlBotIncomingTxAutodeclineEnable"
                        render={({ field: { value, name, onChange } }) => (
                          <>
                            <ToggleInput
                              name={name}
                              title="Blockchain transaction verification warning auto decline"
                              onChange={(e) => {
                                onChange(e.target.checked);

                                if (
                                  fields.amlBotIncomingTxAutodeclineValue &&
                                  !e.target.checked
                                ) {
                                  setValue(
                                    'amlBotIncomingTxAutodeclineValue',
                                    '',
                                  );
                                }

                                if (
                                  errors?.amlBotIncomingTxAutodeclineValue
                                    ?.message &&
                                  !e.target.checked
                                ) {
                                  clearErrors([
                                    'amlBotIncomingTxAutodeclineValue',
                                  ]);
                                }
                              }}
                              checked={value}
                            />
                            {errors?.amlBotIncomingTxAutodeclineEnable
                              ?.message && (
                              <div className={cn.settingError}>
                                {
                                  errors?.amlBotIncomingTxAutodeclineEnable
                                    ?.message
                                }
                              </div>
                            )}
                          </>
                        )}
                      />
                      {fields?.amlBotIncomingTxAutodeclineEnable && (
                        <div
                          className={
                            cn.amlBotAdditionalSettingsAdditionalWrapper
                          }
                        >
                          <Controller
                            control={control}
                            name="amlBotIncomingTxAutodeclineValue"
                            render={({ field: { value, name, onChange } }) => (
                              <InputText
                                size={InputSize.SMALL}
                                name={name}
                                value={value as string}
                                placeholder="Enter risk score from 0 to 100"
                                error={
                                  errors?.amlBotIncomingTxAutodeclineValue
                                    ?.message
                                }
                                onChange={(e) => {
                                  if (
                                    errors?.amlBotIncomingTxAutodeclineValue
                                      ?.message
                                  ) {
                                    clearErrors([
                                      'amlBotIncomingTxAutodeclineValue',
                                    ]);
                                  }

                                  if (Number(e.target.value) > 100) {
                                    onChange('100');
                                  } else if (Number(e.target.value) < 0) {
                                    onChange('0');
                                  } else {
                                    onChange(e);
                                  }
                                }}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className={`${cn.formGroup} ${cn.maxWidth}`}>
                <Controller
                  control={control}
                  name="amlBotTransactionsTypes"
                  render={({ field: { value, name, onChange } }) => (
                    <Select
                      title="Transaction types"
                      name={name}
                      value={value}
                      mode={SelectType.MULTIPLE}
                      list={transactionsTypes
                        ?.filter(
                          (item) =>
                            item.name === 'OUTGOING' ||
                            item.name === 'INCOMING',
                        )
                        ?.map((item) => ({
                          name: getTransactionTypeName(item.name),
                          value: item.id,
                        }))}
                      onChange={onChange}
                      placeholder="Select transaction types"
                      error={errors?.amlBotTransactionsTypes?.message}
                      fullWidth
                    />
                  )}
                />
              </div>
              <div className={`${cn.formGroup} ${cn.maxWidth}`}>
                <Controller
                  control={control}
                  name="amlBotCurrencies"
                  render={({ field: { value, name, onChange } }) => (
                    <AutocompleteMultiple
                      title="Currencies"
                      name={name}
                      value={value}
                      mode={SelectType.MULTIPLE}
                      list={activeCurrencies?.map((item) => ({
                        name: `${item.name} (${item.description})`,
                        value: item.id,
                      }))}
                      selectListPosition={SelectListPosition.TOP}
                      onChange={onChange}
                      placeholder="Select currencies"
                      error={errors?.amlBotCurrencies?.message}
                      fullWidth
                    />
                  )}
                />
              </div>
              {activePermissions?.includes(PermissionEnum.CASP_ALIAS_LIST) && (
                <div className={cn.caspAliasesWrapper}>
                  {(caspAliasesIsLoasing || caspAliasesIsFetching) && (
                    <div className={cn.caspAliasesLoader}>
                      <IconLoader />
                    </div>
                  )}
                  {!caspAliasesIsLoasing && !caspAliasesIsFetching && (
                    <>
                      <div className={cn.caspAliasesTitle}>
                        CASP Connections
                      </div>
                      {caspAliases &&
                        !caspAliases?.length &&
                        !caspAliasesList?.length && (
                          <div className={cn.caspAliasesEmpty}>
                            <IconChain className={cn.chainIcon} />
                            <div className={cn.caspAliasesEmptyDescription}>
                              No CASP mappings defined yet. Add mappings to
                              connect internal CASP IDs with provider chains
                            </div>
                            <div className={cn.addCaspAliasButtonWrapper}>
                              <ButtonSecondary
                                size={ButtonSize.SMALL}
                                icon={<IconExpand />}
                                text="Add mapping rule"
                                className={`button-white ${cn.addCaspAliasButton}`}
                                onClick={() => {
                                  const nextCaspAlisesList = (
                                    [] as CASPAliasFormValues[]
                                  ).concat(caspAliasesList);

                                  nextCaspAlisesList.push({
                                    id: `casp-alias-${uuidv4()}`,
                                    caspId: '',
                                    alias: '',
                                    source: 'AML_BOT',
                                    mode: 'lite',
                                  });

                                  setCaspAliasesList(nextCaspAlisesList);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      {caspAliases?.map((item, index) => (
                        <CaspAlias
                          key={item.id}
                          caspAlias={item}
                          caspAliasesList={caspAliasesList}
                          setCaspAliasesList={setCaspAliasesList}
                          index={index + 1}
                          casp={casp}
                          onDeleteCaspAlias={onDeleteCaspAlias}
                          amlBotAssets={amlBotAssets}
                          showAddButton={Boolean(
                            !caspAliasesList.length &&
                            caspAliases.length === index + 1,
                          )}
                          selectedCaspAliasId={selectedCaspAliasId}
                          deleteCaspAliasInProgress={deleteCaspAliasInProgress}
                        />
                      ))}
                      {caspAliasesList?.length > 0 &&
                        caspAliasesList?.map((item, index) => (
                          <CaspAlias
                            key={item.id}
                            caspAlias={item}
                            caspAliasesList={caspAliasesList}
                            setCaspAliasesList={setCaspAliasesList}
                            index={index + 1}
                            casp={casp}
                            onDeleteCaspAlias={onDeleteCaspAlias}
                            creationCaspAliasErrorsList={
                              creationCaspAliasErrors
                            }
                            amlBotAssets={amlBotAssets}
                            creationCaspAliasErrors={creationCaspAliasErrors?.find(
                              (error) => item.id === error.id,
                            )}
                            setCreationCaspAliasErrors={
                              setCreationCaspAliasErrors
                            }
                          />
                        ))}
                    </>
                  )}
                </div>
              )}
            </div>
            <div className={cn.beneficiariesSettingsBlock}>
              <div className={cn.beneficiariesSettingsTitle}>
                CryptoSwift Integration
              </div>
              <div className={`${cn.formGroup} ${cn.requiredFromGroup}`}>
                <Controller
                  control={control}
                  name="cryptoSwiftEnabled"
                  render={({ field: { value, name, onChange } }) => (
                    <>
                      <ToggleInput
                        name={name}
                        title="Enable CryptoSwift integration"
                        onChange={(e) => onChange(e.target.checked)}
                        checked={value}
                      />
                      {errors?.cryptoSwiftEnabled?.message && (
                        <div className={cn.settingError}>
                          {errors?.cryptoSwiftEnabled?.message}
                        </div>
                      )}
                    </>
                  )}
                />
                <div className={cn.cryptoSwiftHint}>
                  <IconInfo width={16} height={16} />
                  Applies to incoming transactions only. When disabled,
                  transactions proceed to “Proc” without CryptoSwift
                  verification.
                </div>
              </div>
            </div>
            {globalError && (
              <div className={cn.saveGlobalError}>{globalError}</div>
            )}
            <ButtonPrimary
              text={
                editIsLoading || createCaspAliasInProgress
                  ? 'Loading...'
                  : 'Save'
              }
              className={cn.saveBeneficiariesSettings}
              disabled={!isDirty || editIsLoading || createCaspAliasInProgress}
              onClick={onSubmit}
              bold
            />
          </>
        )}
        {errorList && (
          <div className={cn.loadingError}>
            <div className={cn.loadingErrorTitle}>Unable to load data</div>
            <div className={cn.loadingErrorText}>{errorList}</div>
          </div>
        )}
      </div>
    );
  }

  return <div className={cn.beneficiariesSettingsWrapper}>{content}</div>;
};

export default TransactionOptions;
